import { createTemplateCloneRestorer } from './primitives/restoreClonedTemplates'
import { createDeathWatcher } from './primitives/watchAvatarDeath'
import { BehaviorHandler, HudStyle, MissionBehaviors } from './types'

/**
 * RJS07（サバイバル: なんびょう いきのこれるかな）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS07.rbxl）のギミックを移植する:
 * - ServerScriptService/GameOverScript: 死亡（Humanoid.Died）で一度だけ
 *   FinishEvent を発火する（isGameOver ガード。再死亡では更新されない）。
 * - StarterGui/GUILocalScript: FinishEvent 受信で ScreenGui を有効化し、
 *   「(floor(time()))びょう いきのこった！」（区切りは全角スペース）を表示する
 *   （time() はゲーム開始からの経過秒）。ScreenGui は初期 Enabled=false
 *   （死亡まで何も出ない）。
 * - ServerStorage の ②ボール / ③はしら: 学習者プログラム用のテンプレート
 *   （配下の Script は空 = 学習者枠）。実機では ServerStorage に居て見えない。
 *
 * プレビューでの近似・意図的な差異:
 * - 死亡検知は Humanoid.Died の代わりに体力 0 の毎フレーム監視で行う。
 *   奈落落下（void）死はプレビューでは体力 0 を経ないため検知できないが、
 *   アリーナは Wall フォルダの壁で全周囲まれており通常は到達しない。
 * - time() は performance.now() ベースのプレイ開始からの経過秒で代替する。
 * - PlayerRemoving 経路の gameOver（退室時）はプレビューに退室が無いため対象外。
 * - BGM はシーン JSON の bgm 機構の担当（ビヘイビア対象外）。実機の 'Really Fast'
 *   はプロダクト指定の Sunrise（bgm-overrides.json）へ差し替え済み。
 *   ①スタートちてん の子 Sound は学習者の音ブロック対象（シーン変換の担当）。
 *
 * 未対応: なし（Tool・装備系のギミックはこのミッションに存在しない）。
 */

/** 学習者プログラム用テンプレート（実機では ServerStorage）のオブジェクト名。 */
const TEMPLATE_NAMES: ReadonlyArray<string> = ['②ボール', '③はしら']

/**
 * 生存記録の文言の後半（Lua: TextLabel.Text = math.floor(time()) .. 本値。
 * 「びょう」の後ろの区切りは実機どおり全角スペース）。
 */
const SURVIVED_TEXT_SUFFIX = 'びょう　いきのこった！'

/** 生存記録 GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const SURVIVED_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#001F00',
  border: '#FF5500',
  borderWidth: 6,
  wide: true,
}

export const createRjs07Behaviors = (): MissionBehaviors => {
  /**
   * テンプレート（②ボール/③はしら）の隠蔽 + 学習者の control_clone 複製への
   * CanCollide/CanTouch 復元（詳細は primitives/restoreClonedTemplates.ts）。
   */
  const templateRestorer = createTemplateCloneRestorer(TEMPLATE_NAMES)
  const manageTemplates: BehaviorHandler = (ctx) => {
    templateRestorer.concealAndSnapshot(ctx.world)
    return templateRestorer.watchClones(ctx)
  }

  /**
   * 死亡監視（Lua: GameOverScript の Humanoid.Died → GUILocalScript の clear）。
   * 体力 0 を検知したら、プレイ開始からの経過秒（Lua: time()）つきの
   * 生存記録を一度だけ表示する
   * （Lua: GameOverScript の isGameOver。一度立つと二度と表示は更新されない）。
   */
  const watchDeath: BehaviorHandler = (ctx) => {
    const startMs = performance.now()
    return createDeathWatcher({
      once: true,
      onDeath: () => {
        const survivedSeconds = Math.floor((performance.now() - startMs) / 1000)
        // Lua: TextLabel.Text = math.floor(time()) .. SURVIVED_TEXT_SUFFIX
        ctx.setHudText?.(
          `${survivedSeconds}${SURVIVED_TEXT_SUFFIX}`,
          SURVIVED_HUD_STYLE,
        )
      },
    })(ctx)
  }

  return {
    onPlay: [manageTemplates, watchDeath],
    // ②ボール/③はしら は実機では ServerStorage（Script 非実行）。本体では
    // 学習者プログラムを起動しない（クローンにだけ起動する）。
    serverStorageTemplates: [...TEMPLATE_NAMES],
  }
}
